/**
 * Nosto Integration
 * Event dispatching and theme integrations for Nosto-powered features.
 * Last Updated: 2026-02-09
 *
 * Features:
 *   - Nosto API event dispatching (nosto:postrender)
 *   - Empty recommendation section hiding
 *   - Product card swatch carousel (overflow detection, bounded scroll)
 *
 * NOTE: Early initiation is now in theme.liquid head (after content_for_header)
 */

(function () {
  'use strict';

  if (window.__nostoIntegrationLoaded) return;
  window.__nostoIntegrationLoaded = true;

  // =============================================================================
  // Configuration
  // =============================================================================
  var CONFIG = {
    NOSTO_POLL_INTERVAL_MS: 250,
    NOSTO_POLL_MAX_ATTEMPTS: 40,
    HIDE_EMPTY_DELAY_MS: 150, // Delay for dynamic content to finish rendering
    EVENT_DEBOUNCE_MS: 100,   // Prevent double-firing from taggingsent + postrender

    // Swatch carousel configuration
    SWATCH_OVERFLOW_TOLERANCE: 2,  // Pixels of tolerance for sub-pixel rounding in browsers
    SWATCH_GAP_PX: 8,              // Gap between swatches (matches CSS gap: 8px)
    SWATCH_RESIZE_DEBOUNCE_MS: 250, // Debounce delay for window resize re-evaluation
  };

  // =============================================================================
  // Debounce Helper
  // =============================================================================
  var lastDispatchTimestamp = 0;

  // =============================================================================
  // Document Ready Helper
  // =============================================================================
  function onReady(cb) {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', cb, { once: true });
    } else {
      cb();
    }
  }

  // =============================================================================
  // Nosto API Integration
  // Dispatches nosto:postrender events for other integrations (Yotpo, etc.)
  // =============================================================================

  // Safety class: only sections with this class can be hidden
  // This prevents accidentally hiding critical sections like main-product
  var SAFE_SECTION_CLASS = 'ns-product-recommendations-section';

  /**
   * Safely hide a Nosto placement section
   * ONLY hides dedicated recommendation sections, never app blocks in other sections
   */
  function safeHideSection(el) {
    if (!el) return;

    // Find the parent shopify-section
    var section = el.closest('.shopify-section');
    if (!section) {
      // No section wrapper - just hide the element itself
      el.style.display = 'none';
      return;
    }

    // SAFETY CHECK: Only hide if it's a dedicated recommendation section
    if (section.classList.contains(SAFE_SECTION_CLASS)) {
      section.style.display = 'none';
    } else {
      // Not a safe section - only hide the nosto_element itself
      el.style.display = 'none';
    }
  }

  /**
   * Hide unfilled Nosto placements reported by the API
   */
  function hideUnfilledPlacements(unFilledIds) {
    if (!Array.isArray(unFilledIds) || unFilledIds.length === 0) return;

    unFilledIds.forEach(function(id) {
      try {
        var el = document.getElementById(id);
        safeHideSection(el);
      } catch (e) {
        // Continue processing other placements if one fails
      }
    });
  }

  /**
   * Hide empty recommendation sections
   * ONLY targets dedicated ns-product-recommendations-section sections
   *
   * Detection methods (in priority order):
   * 1. .nosto-empty-result marker (injected by Nosto template when no products)
   * 2. Fallback: check for absence of product content
   */
  function hideEmptyRecommendationSections() {
    var sections = document.querySelectorAll('.shopify-section.' + SAFE_SECTION_CLASS);

    sections.forEach(function(section) {
      try {
        // Skip if already hidden
        if (section.style.display === 'none') return;

        var nostoEl = section.querySelector('.nosto_element');
        if (!nostoEl) return;

        // Method 1: Check for explicit empty marker from Nosto template
        var hasEmptyMarker = nostoEl.querySelector('.nosto-empty-result');
        if (hasEmptyMarker) {
          section.style.display = 'none';
          return;
        }

        // Method 2: Fallback - check for absence of product content
        // Only applies if Nosto has rendered something (not :empty)
        if (nostoEl.children.length > 0) {
          var hasContent = nostoEl.querySelector('.ProductItem, .product-item, .blaze-track > *, .slick-track > *');
          if (!hasContent) {
            section.style.display = 'none';
          }
        }
      } catch (e) {
        // Continue processing other sections if one fails
      }
    });
  }

  function setupNostoJsListener() {
    if (typeof window.nostojs !== 'function') return;

    window.nostojs(function(api) {
      /**
       * Dispatch custom event for other integrations (Yotpo, quick-buy-modal, etc.)
       * Includes debounce to prevent double-firing when both taggingsent and postrender fire
       */
      function dispatchPostrenderEvent(response) {
        // Debounce: prevent double-firing from taggingsent + postrender events
        var now = Date.now();
        if (now - lastDispatchTimestamp < CONFIG.EVENT_DEBOUNCE_MS) return;
        lastDispatchTimestamp = now;

        var filled = Array.isArray(response && response.filledElements) ? response.filledElements : [];
        var unFilled = Array.isArray(response && response.unFilledElements) ? response.unFilledElements : [];

        // Hide placements that Nosto reports as unfilled
        hideUnfilledPlacements(unFilled);

        // Check for empty recommendation sections after Nosto finishes rendering
        // Delay ensures dynamic content has time to load
        setTimeout(hideEmptyRecommendationSections, CONFIG.HIDE_EMPTY_DELAY_MS);

        document.dispatchEvent(new CustomEvent('nosto:postrender', {
          detail: {
            placements: filled,
            unFilledElements: unFilled
          }
        }));
      }

      // taggingsent fires after recommendations are rendered (works in all response modes)
      api.listen('taggingsent', function(response) {
        dispatchPostrenderEvent(response);
      });

      // postrender only works in HTML response mode (backup)
      api.listen('postrender', function(event) {
        var filled = [];

        // Try different possible structures
        if (Array.isArray(event && event.filledElements)) {
          filled = event.filledElements;
        } else if (Array.isArray(event && event.filled)) {
          filled = event.filled;
        } else if (event && typeof event === 'object') {
          filled = Object.values(event).filter(function(id) {
            return typeof id === 'string' && id.length > 0;
          });
        }

        if (filled.length > 0) {
          dispatchPostrenderEvent({ filledElements: filled, unFilledElements: [] });
        } else {
          dispatchPostrenderEvent({ filledElements: [], unFilledElements: [] });
        }
      });
    });
  }

  // =============================================================================
  // SPA/Dynamic Section Support
  // Observes DOM for dynamically added Nosto sections and processes them
  // =============================================================================
  function setupMutationObserver() {
    if (typeof MutationObserver === 'undefined') return;

    var observer = new MutationObserver(function(mutations) {
      var hasNewNostoSections = mutations.some(function(mutation) {
        return Array.from(mutation.addedNodes).some(function(node) {
          if (node.nodeType !== 1) return false;
          // Check if the added node is or contains a Nosto recommendation section
          return node.classList && node.classList.contains(SAFE_SECTION_CLASS) ||
                 node.querySelector && node.querySelector('.' + SAFE_SECTION_CLASS);
        });
      });

      if (hasNewNostoSections) {
        // Delay to allow Nosto to render content in new sections
        setTimeout(hideEmptyRecommendationSections, CONFIG.HIDE_EMPTY_DELAY_MS);
      }
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true
    });
  }

  // =============================================================================
  // Product Swatches - Overflow Detection, Bounded Scroll & Click Guard
  // =============================================================================
  //
  // Handles server-side rendered color swatches with:
  // - Overflow detection (shows arrow button when swatches exceed container width)
  // - Bounded scroll with click guard (prevents rapid-tap race conditions)
  // - At end of track → smooth slide back to position 0
  // - CSS scroll-snap for clean landing positions
  // - Responsive re-evaluation (adjusts on window resize)
  // - Dynamic card support (initializes swatches in Nosto-injected cards)
  //
  // SCROLL ARCHITECTURE:
  // --------------------
  // No DOM cloning. The track contains only the original swatches. Each button
  // click scrolls by one swatch width + gap. Forward scrolls have no click guard
  // (overlapping scrollBy calls are safe). When the user reaches the last swatch
  // and clicks next, the track smoothly slides back to position 0 with a click
  // guard (isScrolling flag, 400ms) to prevent re-entry during the reset animation.
  //
  // DATA ATTRIBUTES:
  // ----------------
  // data-swatch-initialized
  //   - Set on: .pl-swatches-collection (container)
  //   - Purpose: Marks container as processed to prevent double-initialization
  //   - Value: "true" (presence indicates initialized)
  //
  // CSS LOCATION: assets/nosto-integration.css (PRODUCT CARD SWATCHES section)
  // =============================================================================

  /**
   * WeakMap to track ResizeObservers per container.
   * Using WeakMap allows garbage collection when containers are removed from DOM.
   * @type {WeakMap<Element, ResizeObserver>}
   */
  var swatchObservers = new WeakMap();

  /**
   * Check if track has overflow and show button if needed.
   * Called once per container during initialization.
   *
   * @param {HTMLElement} container - The .pl-swatches-collection element
   * @param {HTMLElement} track - The .pl-swatches-collection__track element
   * @param {HTMLElement} btn - The .pl-swatches-collection__btn element
   * @returns {void}
   */
  function checkSwatchOverflow(container, track, btn) {
    // Skip if already initialized or track not ready
    if (container.hasAttribute('data-swatch-initialized')) return;
    if (track.clientWidth === 0) return; // Not laid out yet

    var hasOverflow = track.scrollWidth > track.clientWidth + CONFIG.SWATCH_OVERFLOW_TOLERANCE;

    if (hasOverflow) {
      btn.classList.add('pl-swatches-collection__btn--visible');
      btn.setAttribute('aria-hidden', 'false');
    }

    container.setAttribute('data-swatch-initialized', 'true');

    // Disconnect observer once initialized
    var observer = swatchObservers.get(container);
    if (observer) {
      observer.disconnect();
      swatchObservers.delete(container);
    }
  }

  /**
   * Initialize a single swatch container.
   * Uses ResizeObserver to wait for layout if container has no width yet.
   * Guards against double-initialization via data-swatch-initialized attribute.
   *
   * @param {HTMLElement} container - The .pl-swatches-collection element to initialize
   * @returns {void}
   */
  function initSwatchContainer(container) {
    if (container.hasAttribute('data-swatch-initialized')) return;
    if (swatchObservers.has(container)) return; // Already observing

    var track = container.querySelector('.pl-swatches-collection__track');
    var btn = container.querySelector('.pl-swatches-collection__btn');
    if (!track || !btn) return;

    // If track already has width, check immediately
    if (track.clientWidth > 0) {
      checkSwatchOverflow(container, track, btn);
      return;
    }

    // Otherwise, use ResizeObserver to wait for layout
    if (typeof ResizeObserver !== 'undefined') {
      var observer = new ResizeObserver(function() {
        checkSwatchOverflow(container, track, btn);
      });
      observer.observe(track);
      swatchObservers.set(container, observer);
    }
  }

  /**
   * Initialize all swatch containers currently in the DOM.
   * Called on page load and after Nosto renders recommendations.
   *
   * @returns {void}
   */
  function initAllSwatchContainers() {
    var containers = document.querySelectorAll('.pl-swatches-collection');
    containers.forEach(function(container) {
      initSwatchContainer(container);
    });
  }

  /**
   * Setup MutationObserver to detect dynamically added swatch containers.
   * Required because Nosto injects product cards after initial page load.
   * Observes document.body for childList changes in the entire subtree.
   *
   * @returns {void}
   */
  function setupSwatchMutationObserver() {
    if (typeof MutationObserver === 'undefined') return;

    var observer = new MutationObserver(function(mutations) {
      mutations.forEach(function(mutation) {
        mutation.addedNodes.forEach(function(node) {
          if (node.nodeType !== 1) return;

          // Check if the added node is a swatch container
          if (node.classList && node.classList.contains('pl-swatches-collection')) {
            initSwatchContainer(node);
          }

          // Check if the added node contains swatch containers
          if (node.querySelectorAll) {
            var containers = node.querySelectorAll('.pl-swatches-collection');
            containers.forEach(function(container) {
              initSwatchContainer(container);
            });
          }
        });
      });
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true
    });
  }

  /**
   * Setup delegated click handler for swatch navigation buttons.
   * Uses event delegation (single listener on document) for efficiency.
   *
   * Behavior:
   * - Forward scrolls have no click guard (overlapping scrollBy calls are safe)
   * - Click guard (isScrolling flag, 400ms) only on reset-to-start to prevent re-entry
   * - Reads gap from CSS via getComputedStyle for gap-proof stride calculation
   * - Scrolls by one swatch width per click (smooth via JS, not CSS)
   * - When remaining distance < stride → smooth slide back to position 0
   * - CSS scroll-snap ensures clean landing positions
   *
   * @returns {void}
   */
  var isScrolling = false;
  function setupSwatchButtonHandler() {
    document.addEventListener('click', function(e) {
      var btn = e.target.closest('.pl-swatches-collection__btn');
      if (!btn) return;

      e.preventDefault();

      // Click guard — prevent re-entry while scroll animation is in progress
      if (isScrolling) return;

      var container = btn.closest('.pl-swatches-collection');
      if (!container) return;

      var track = container.querySelector('.pl-swatches-collection__track');
      if (!track) return;

      // Calculate stride from first swatch width + actual CSS gap
      var firstSwatch = track.querySelector('.pl-swatches-collection__link');
      if (!firstSwatch) return;

      var gap = parseFloat(getComputedStyle(track).columnGap) || CONFIG.SWATCH_GAP_PX;
      var stride = firstSwatch.offsetWidth + gap;

      // Check if remaining scrollable distance is less than one full stride
      var maxScroll = track.scrollWidth - track.clientWidth;
      var remaining = maxScroll - track.scrollLeft;

      if (remaining < stride) {
        // Not enough room for a full swatch scroll — smooth slide back to start
        isScrolling = true;
        track.scrollTo({ left: 0, behavior: 'smooth' });
        setTimeout(function() {
          isScrolling = false;
        }, 400);
        return;
      }

      // Forward scroll — no click guard needed; overlapping scrollBy calls are safe
      track.scrollBy({ left: stride, behavior: 'smooth' });
    });
  }

  /**
   * Re-evaluate overflow state on a previously initialized container.
   * Called during window resize to handle responsive layout changes.
   *
   * Handles two scenarios:
   * 1. Gained overflow: Shows button
   * 2. Lost overflow: Hides button, resets scroll position
   *
   * @param {HTMLElement} container - The .pl-swatches-collection element to re-evaluate
   * @returns {void}
   */
  function reevaluateSwatchOverflow(container) {
    var track = container.querySelector('.pl-swatches-collection__track');
    var btn = container.querySelector('.pl-swatches-collection__btn');
    if (!track || !btn) return;

    var hasOverflow = track.scrollWidth > track.clientWidth + CONFIG.SWATCH_OVERFLOW_TOLERANCE;
    var currentlyHasButton = btn.classList.contains('pl-swatches-collection__btn--visible');

    if (hasOverflow && !currentlyHasButton) {
      // Gained overflow — show button
      btn.classList.add('pl-swatches-collection__btn--visible');
      btn.setAttribute('aria-hidden', 'false');

    } else if (!hasOverflow && currentlyHasButton) {
      // Lost overflow — hide button and reset scroll
      btn.classList.remove('pl-swatches-collection__btn--visible');
      btn.setAttribute('aria-hidden', 'true');
      track.scrollLeft = 0;
    }
  }

  /**
   * Setup debounced window resize handler.
   * Re-evaluates overflow state on all initialized containers when viewport changes.
   * Debounce prevents excessive recalculation during resize drag.
   *
   * @returns {void}
   */
  var resizeTimeout = null;
  function setupSwatchResizeHandler() {
    window.addEventListener('resize', function() {
      if (resizeTimeout) {
        clearTimeout(resizeTimeout);
      }
      resizeTimeout = setTimeout(function() {
        var containers = document.querySelectorAll('.pl-swatches-collection[data-swatch-initialized]');
        containers.forEach(function(container) {
          reevaluateSwatchOverflow(container);
        });
      }, CONFIG.SWATCH_RESIZE_DEBOUNCE_MS);
    });
  }

  // =============================================================================
  // Initialization
  // =============================================================================
  onReady(function() {
    // Poll for nostojs availability
    var tries = 0;
    var pollInterval = setInterval(function() {
      tries += 1;
      if (typeof window.nostojs === 'function') {
        clearInterval(pollInterval);
        setupNostoJsListener();
      } else if (tries >= CONFIG.NOSTO_POLL_MAX_ATTEMPTS) {
        clearInterval(pollInterval);
      }
    }, CONFIG.NOSTO_POLL_INTERVAL_MS);

    // Setup observer for dynamically added sections (SPA support)
    setupMutationObserver();

    // Setup swatch functionality
    setupSwatchButtonHandler();
    setupSwatchMutationObserver(); // Watch for dynamically added Nosto cards
    setupSwatchResizeHandler();    // Handle responsive overflow changes
    initAllSwatchContainers();
  });

  // Initialize swatches after Nosto renders dynamic cards
  document.addEventListener('nosto:postrender', function() {
    initAllSwatchContainers();
  });
})();
